/* eslint-disable react/no-unused-prop-types */
import { faCarSide } from '@fortawesome/pro-regular-svg-icons/faCarSide';
import { faLocationArrow } from '@fortawesome/pro-regular-svg-icons/faLocationArrow';
import { faPencilAlt } from '@fortawesome/pro-regular-svg-icons/faPencilAlt';
import { faSearch } from '@fortawesome/pro-solid-svg-icons/faSearch';
import { faTimes } from '@fortawesome/pro-solid-svg-icons/faTimes';
import {
  CATEGORY_OPTIONS,
  CURRENCY_OPTIONS,
  EXPAND_OPTIONS,
  HAS_BEDROOMS_FILTER,
  PRICE_OPTIONS,
  RADIUS_OPTIONS,
  RENTAL_PRICE_OPTIONS,
} from '@propertypal/shared/src/constants/filterOptions';
import useEventListener from '@propertypal/shared/src/hooks/useEventListener';
import { RecentSearch } from '@propertypal/shared/src/hooks/useRecentSearches';
import { UseSearchState } from '@propertypal/shared/src/hooks/useSearchState';
import { selectProperties } from '@propertypal/shared/src/reducers/properties';
import { initialState, loadSavedSearch, SearchState, setSearch } from '@propertypal/shared/src/reducers/search';
import { useAppDispatch } from '@propertypal/shared/src/reducers/store';
import filterIcon from '@propertypal/shared/src/resources/icons/search/filter-icon-black.svg?url';
import filterOrangeIcon from '@propertypal/shared/src/resources/icons/search/filter-icon-orange.svg?url';
import currencyDirect from '@propertypal/shared/src/resources/logos/currencies-direct-logo-dark.png';
import { trackGaEvent, trackGaLink } from '@propertypal/shared/src/services/analytics';
import { PropertySearchResult } from '@propertypal/shared/src/types/propertySearch';
import { getCurrencySymbol } from '@propertypal/shared/src/utils/floatToCurrency';
import { usePropertyStyles } from '@propertypal/shared/src/utils/property/getPropertyStyles';
import { getCategoryOption, getPriceSuffix } from '@propertypal/shared/src/utils/search/category';
import { useFilters } from '@propertypal/shared/src/utils/search/getFilters';
import getFilterTitles, { getTotalFilters } from '@propertypal/shared/src/utils/search/getFilterTitles';
import { Filters } from '@propertypal/shared/src/utils/search/getNlpFilters';
import { Suggestion } from '@propertypal/shared/src/utils/search/groupTokensByType';
import Button from '@propertypal/web-ui/src/buttons/Button';
import Tag from '@propertypal/web-ui/src/buttons/Tag';
import WhiteButton from '@propertypal/web-ui/src/buttons/WhiteButton';
import FontAwesomeIcon from '@propertypal/web-ui/src/icons/FontAwesomeIcon';
import CheckItem from '@propertypal/web-ui/src/inputs/CheckItem';
import SearchInput from '@propertypal/web-ui/src/inputs/SearchInput';
import SelectInput from '@propertypal/web-ui/src/inputs/SelectInput';
import { Column, Row, RowCenter, RowWrap } from '@propertypal/web-ui/src/layout';
import useMediaQuery from '@propertypal/web-ui/src/media-query/useMediaQuery';
import { InputLabel, SubHeading } from '@propertypal/web-ui/src/typography';
import { useRouter } from 'next/router';
import React, { FormEvent, FunctionComponent, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ScaleLoader from 'react-spinners/ScaleLoader';
import { useTheme } from 'styled-components';
import getCurrentLocation from '../../../services/location';
import togglePageScroll from '../../../utils/togglePageScroll';
import BedInputs from './BedInputs';
import IncludeExcludeInputs from './IncludeExcludeInputs';
import PreferenceInputs from './PreferenceInputs';
import PriceInputs from './PriceInputs';
import {
  SearchBarForm,
  SearchModal,
  InputContainer,
  InputRow,
  InputColumn,
  FilterContainer,
  FiltersPane,
  FiltersButton,
  ModalFooter,
  CloseIcon,
  ScrollDiv,
  filterBreakpoint,
  CurrencyToggle,
  SmartFiltersWrapper,
  FilterPaneContent,
  SearchButton,
  DropItem,
  SearchInputIcon,
  Dropdown,
  FiltersDropdown,
  FiltersBottom,
  FiltersScroll,
  DropBox,
  SearchModalHeader,
  StyledSelect,
  TotalIcon,
  PartnershipBar,
  SmallPartnership,
} from './SearchBar.style';
import SmartInputs from './SmartInputs';
import StyleInputs from './StyleInputs';

interface Props {
  searchModalOpen: boolean;
  onSearchModalToggle: (open: boolean) => void;
  loading: boolean;
  imageLocator: string;
  searchState: UseSearchState;
  onSubmit: () => void;
  onSaveSearchClick: () => void;
  onDrawSearchClick?: () => void;
  onTravelTimeClick: () => void;
  recentSearches: RecentSearch[];
  removeRecentSearch: (search: RecentSearch) => void;
  loggedIn: boolean;
  onCloseToSearch: (polyIds: number[]) => Promise<boolean>;
  importantPlaces?: PropertySearchResult['importantPlaces'];
  onMap?: boolean;
}

const currencyInput = (filters: SearchState['filters'], handleFilterChange: UseSearchState['handleFilterChange']) => {
  return (
    <CurrencyToggle
      label="Currency"
      options={CURRENCY_OPTIONS}
      onChange={handleFilterChange('currency')}
      value={filters.currency || 'GBP'}
    />
  );
};

const furnishingInputs = (
  filters: SearchState['filters'],
  handleFilterChange: UseSearchState['handleFilterChange'],
  isMobile?: boolean,
) => {
  return (
    <>
      <InputColumn>
        <CheckItem
          ariaLabel="Filter by unfurnished properties"
          containerStyle={{ marginRight: 20 }}
          label="Unfurnished"
          checked={filters.unfurnished}
          onValueChange={handleFilterChange('unfurnished')}
          testID="checkItem-unfurnished"
        />

        <CheckItem
          ariaLabel="Filter by furnished properties"
          containerStyle={{ marginRight: isMobile ? 0 : 20 }}
          label="Furnished"
          checked={filters.furnished}
          onValueChange={handleFilterChange('furnished')}
          testID="checkItem-furnished"
        />
      </InputColumn>

      <InputColumn>
        <CheckItem
          ariaLabel="Filter by partially furnished properties"
          containerStyle={{ marginRight: 20 }}
          label="Partially Furnished"
          checked={filters.partiallyFurnished}
          onValueChange={handleFilterChange('partiallyFurnished')}
          testID="checkItem-partially-furnished"
        />

        <CheckItem
          ariaLabel="Filter by optionally furnished properties"
          containerStyle={{ marginRight: isMobile ? 0 : 20 }}
          label="Optional"
          checked={filters.optionalFurnished}
          onValueChange={handleFilterChange('optionalFurnished')}
          testID="checkItem-optional-furnished"
        />
      </InputColumn>
    </>
  );
};

const styleTags = (filters: SearchState['filters'], handleClearAttribute: UseSearchState['handleClearAttribute']) => {
  if (filters.filter.length > 0) {
    return (
      <>
        <InputLabel mt={20}>Additional filters</InputLabel>

        <RowWrap>
          {filters.filter.map(({ description, filterValue }) => (
            <Tag
              containerStyle={{ marginRight: 15, marginBottom: 5 }}
              onCloseClick={() => handleClearAttribute(filterValue)}
            >
              {description}
            </Tag>
          ))}
        </RowWrap>
      </>
    );
  }

  return null;
};

const SearchBar: FunctionComponent<Props> = (props) => {
  const {
    searchText,
    appFilters,
    showExpand,
    showRadius,
    nlpData,
    nlpFilters,
    handleClearAttribute,
    handleChangeText,
    handleFilterChange,
    handleStyleChange,
    handleClearFilters,
    getLocation,
    fetchingLocation,
  } = props.searchState;

  const properties = useSelector(selectProperties);
  const isTablet = useMediaQuery({ query: `(${filterBreakpoint})` });
  const theme = useTheme();
  const router = useRouter();
  const dispatch = useDispatch();
  const asyncDispatch = useAppDispatch();
  const inputRef = useRef<HTMLInputElement>(null);
  const [activeFilterPane, setActiveFilterPane] = useState('');
  const filters = { ...appFilters, ...nlpFilters };
  const filterQuery = useFilters(filters, false);
  const { priceText, bedsText, totalStyles, totalMoreFilters } = getFilterTitles(filters);
  const totalFilters = getTotalFilters(appFilters, true);
  const dropRef = useRef<HTMLDivElement>(null);

  const propertyStyles = usePropertyStyles(filters.category, filters.saleType);
  const currencySymbol = getCurrencySymbol(filters.currency);
  const isRental = filters.saleType === 'rent';
  const isNewHomes = filters.category === 'newhomes';
  const isIE = nlpData?.phrase.roi;
  const showFurnishingOptions = ['residential', 'student'].includes(filters.category) && filters.saleType === 'rent';
  const [minPriceSuffix, fullMinPriceSuffix] = getPriceSuffix(filters, 'min', 'minimum');
  const [maxPriceSuffix, fullMaxPriceSuffix] = getPriceSuffix(filters, 'max', 'maximum');

  const minPriceOptions = isRental
    ? RENTAL_PRICE_OPTIONS(currencySymbol, minPriceSuffix, fullMinPriceSuffix)
    : PRICE_OPTIONS(currencySymbol, minPriceSuffix, fullMinPriceSuffix);
  const maxPriceOptions = isRental
    ? RENTAL_PRICE_OPTIONS(currencySymbol, maxPriceSuffix, fullMaxPriceSuffix)
    : PRICE_OPTIONS(currencySymbol, maxPriceSuffix, fullMaxPriceSuffix);

  const toggleFilterPane = (pane: string) => () => {
    setActiveFilterPane(pane === activeFilterPane ? '' : pane);
  };

  const handleSubmit = async (e?: FormEvent<HTMLFormElement>) => {
    e?.preventDefault();
    inputRef.current?.blur();
    props.onSearchModalToggle(false);

    const latLng = await getLocation(getCurrentLocation, false);

    setActiveFilterPane('');
    dispatch(
      setSearch({
        text: searchText,
        filters: { ...appFilters, ...latLng },
      }),
    );
    props.onSubmit();
  };

  const handleSuggestionClick = (token: Suggestion) => {
    if (token.searchParams) {
      asyncDispatch(loadSavedSearch(token.searchParams, props.imageLocator));
    } else if (token.url) {
      router.push(token.url);
    } else {
      handleChangeText(token.value);
    }
  };

  const handleRecentSearch = (text: string, recentFilters: Filters) => {
    trackGaEvent('recent_search_click', { location: 'Search Bar' });

    dispatch(
      setSearch({
        text,
        filters: { ...initialState.filters, ...recentFilters },
      }),
    );
    props.onSubmit();
  };

  const handleDrawSearch = (position: 'suggestion' | 'inline') => {
    if (props.onDrawSearchClick) {
      trackGaEvent('draw_click', {
        page: 'SRP',
        location: 'Search Box',
        position,
      });
      props.onDrawSearchClick();
    }
  };

  const handleLocationClick = (position: string) => {
    trackGaEvent('current_location_click', {
      page: 'SRP',
      location: 'Search Box',
      position,
    });
    getLocation(getCurrentLocation, true);
  };

  const handleTravelTimeClick = (position: string) => {
    trackGaEvent('travel_time_click', {
      page: 'SRP',
      location: 'Search Box',
      position,
    });
    props.onTravelTimeClick();
  };

  const detectClickOutside = (e: MouseEvent) => {
    // @ts-ignore
    if (dropRef.current && document.body.contains(e.target) && !dropRef.current.contains(e.target)) {
      setActiveFilterPane('');
    }
  };

  useEventListener('click', detectClickOutside);

  useEffect(() => {
    if (props.searchModalOpen) {
      togglePageScroll('#searchModal', false);
    } else {
      togglePageScroll('#searchModal', true);
    }
  }, [props.searchModalOpen]);

  useEffect(() => {
    if (!isTablet && props.searchModalOpen) {
      props.onSearchModalToggle(false);
    }
  }, [isTablet]);

  useEffect(() => {
    return () => togglePageScroll('#searchModal', true);
  }, []);

  useEffect(() => {
    if (activeFilterPane === 'price' || props.searchModalOpen) {
      trackGaEvent('currency_direct_logo_impression', {
        location: 'SRP',
      });
    }
  }, [activeFilterPane, props.searchModalOpen]);

  return (
    <form action="" onSubmit={handleSubmit} role="search">
      <SearchBarForm>
        <InputContainer ref={dropRef}>
          <SearchInput
            containerStyle={{ borderRadius: 20 }}
            inputRef={inputRef}
            onChangeText={handleChangeText}
            value={searchText}
            recentSearches={props.recentSearches}
            onRemoveRecentSearch={props.removeRecentSearch}
            onRecentSearchClick={handleRecentSearch}
            nlpData={nlpData}
            loading={fetchingLocation}
            onSuggestionClick={handleSuggestionClick}
            onLocationClick={handleLocationClick}
            onTravelTimeClick={handleTravelTimeClick}
            onDrawSearchClick={handleDrawSearch}
            hideSearchIcons
            onFocus={() => setActiveFilterPane('')}
          />

          {showRadius && !isTablet && (
            <StyledSelect
              ariaLabel="Select a search radius"
              id="selectRadius"
              disableMaxHeight
              placeholder="Radius"
              options={RADIUS_OPTIONS}
              value={filters.radius}
              onChange={handleFilterChange('radius')}
              testID="selectRadius"
            />
          )}

          {showExpand && !isTablet && (
            <StyledSelect
              ariaLabel="Expand search radius"
              id="expandRadius"
              disableMaxHeight
              placeholder="Expand"
              options={EXPAND_OPTIONS}
              value={filters.expand}
              onChange={handleFilterChange('expand')}
              testID="expandRadius"
            />
          )}

          <DropBox>
            <DropItem
              title={priceText}
              $active={activeFilterPane === 'price'}
              type="button"
              onClick={toggleFilterPane('price')}
              data-testid="priceRangeDropdown"
            >
              {priceText}
            </DropItem>

            {activeFilterPane === 'price' && (
              <Dropdown style={{ maxWidth: 600 }}>
                {currencyInput(filters, handleFilterChange)}

                <PriceInputs
                  filters={filters}
                  minPriceOptions={minPriceOptions}
                  maxPriceOptions={maxPriceOptions}
                  handleFilterChange={handleFilterChange}
                  currencySymbol={currencySymbol}
                />

                <PartnershipBar
                  href="/currency-transfers"
                  onClick={trackGaLink('currencies_direct_logo_click', 'Price Dropdown', 'SRP')}
                >
                  In partnership with <img src={currencyDirect.src} alt="Currencies Direct" />
                </PartnershipBar>
              </Dropdown>
            )}
          </DropBox>

          {HAS_BEDROOMS_FILTER.includes(filters.category) && (
            <DropBox>
              <DropItem
                title={bedsText}
                $active={activeFilterPane === 'bedrooms'}
                type="button"
                onClick={toggleFilterPane('bedrooms')}
                data-testid="bedroomsDropdown"
              >
                {bedsText}
              </DropItem>

              {activeFilterPane === 'bedrooms' && (
                <Dropdown>
                  <BedInputs filters={filters} handleFilterChange={handleFilterChange} />
                </Dropdown>
              )}
            </DropBox>
          )}

          {propertyStyles && propertyStyles.length > 0 && (
            <DropBox>
              <DropItem
                title="Property Type"
                $active={activeFilterPane === 'propertyType'}
                type="button"
                onClick={toggleFilterPane('propertyType')}
                data-testid="propertyTypeDropdown"
              >
                Property Type
              </DropItem>

              {totalStyles > 0 && <TotalIcon>{totalStyles}</TotalIcon>}

              {activeFilterPane === 'propertyType' && (
                <Dropdown>
                  <StyleInputs
                    filters={filters}
                    propertyStyles={propertyStyles}
                    handleStyleChange={handleStyleChange}
                  />
                </Dropdown>
              )}
            </DropBox>
          )}

          <DropBox>
            <DropItem
              title="More Filters"
              $active={activeFilterPane === 'filters'}
              type="button"
              onClick={toggleFilterPane('filters')}
              data-testid="moreFiltersDropdown"
            >
              More Filters
            </DropItem>

            {totalMoreFilters > 0 && <TotalIcon>{totalMoreFilters}</TotalIcon>}

            {activeFilterPane === 'filters' && (
              <FiltersDropdown>
                <FiltersScroll>
                  <SubHeading align="left" mb={15} fontSize={20}>
                    More Filters
                  </SubHeading>

                  <Row flexWrap="wrap" gap={10} mb={15}>
                    <IncludeExcludeInputs
                      filters={filters}
                      isRental={isRental}
                      handleFilterChange={handleFilterChange}
                    />
                  </Row>

                  {showFurnishingOptions && (
                    <>
                      <SubHeading align="left" fontSize={20} mb={15}>
                        Furnishing
                      </SubHeading>

                      <Row style={{ minWidth: 960 }}>{furnishingInputs(filters, handleFilterChange)}</Row>
                    </>
                  )}

                  <Column style={{ width: '100%' }}>{styleTags(filters, handleClearAttribute)}</Column>

                  {['residential', 'newhomes', 'student', 'holidayhomes'].includes(filters.category) && (
                    <>
                      <SmartFiltersWrapper>
                        <SmartInputs
                          filters={filters}
                          handleFilterChange={handleFilterChange}
                          isNewHomes={isNewHomes}
                          searchParams={properties.data?.params}
                          isIE={isIE}
                        />
                      </SmartFiltersWrapper>

                      <PreferenceInputs
                        filters={filters}
                        handleFilterChange={handleFilterChange}
                        isRental={isRental}
                        isNewHomes={isNewHomes}
                      />
                    </>
                  )}
                </FiltersScroll>

                <FiltersBottom>
                  <WhiteButton onClick={handleClearFilters}>Reset</WhiteButton>
                  <Button type="submit">Apply</Button>
                </FiltersBottom>
              </FiltersDropdown>
            )}
          </DropBox>

          {props.onDrawSearchClick && (
            <SearchInputIcon
              aria-label="Draw a search"
              title="Draw a search"
              onClick={() => handleDrawSearch('inline')}
              type="button"
              data-testid="drawSearchButton"
            >
              <FontAwesomeIcon icon={faPencilAlt} size={22} style={{ marginTop: 2 }} />
            </SearchInputIcon>
          )}

          <SearchInputIcon
            aria-label="Search by travel time"
            title="Search by travel time"
            onClick={() => handleTravelTimeClick('inline')}
            type="button"
            data-testid="travelTimeButton"
          >
            <FontAwesomeIcon icon={faCarSide} size={22} style={{ marginTop: 2 }} />
          </SearchInputIcon>

          <SearchInputIcon
            aria-label="Search by current location"
            title="Search by current location"
            onClick={() => handleLocationClick('inline')}
            type="button"
          >
            <FontAwesomeIcon icon={faLocationArrow} size={22} style={{ marginTop: 2 }} />
          </SearchInputIcon>

          <FiltersButton
            aria-label="Filters"
            active={filterQuery !== ''}
            onClick={() => props.onSearchModalToggle(!props.searchModalOpen)}
            data-testid="filtersButton"
            type="button"
          >
            {filterQuery === '' && (
              <img
                src={filterIcon.src}
                width={filterIcon.width}
                height={filterIcon.height}
                alt="filter icon"
                style={{ marginRight: 5 }}
              />
            )}
            {filterQuery !== '' && (
              <img
                src={filterOrangeIcon.src}
                width={filterOrangeIcon.width}
                height={filterOrangeIcon.height}
                alt="filter icon"
                style={{ marginRight: 5 }}
              />
            )}
            <span>Filters</span>
          </FiltersButton>

          <SearchButton aria-label="Search" data-testid="searchSubmitButton" type="submit">
            {props.loading ? (
              <ScaleLoader color="#ffffff" height={20} width={3} />
            ) : (
              <FontAwesomeIcon icon={faSearch} size={18} />
            )}
          </SearchButton>
        </InputContainer>
      </SearchBarForm>

      {isTablet && (
        <SearchModal active={props.searchModalOpen} data-testid="search-modal">
          <SearchModalHeader>
            <SubHeading>Filters</SubHeading>

            <CloseIcon
              data-testid="closeSearchModalButton"
              onClick={() => props.onSearchModalToggle(false)}
              type="button"
            >
              <FontAwesomeIcon icon={faTimes} color={theme.textDark} style={{ fontSize: 30 }} />
            </CloseIcon>
          </SearchModalHeader>

          <ScrollDiv id="searchModal">
            {/* modal open check prevents contents being loaded initially. */}
            {/* delayed loading of style inputs helps page speed */}
            {props.searchModalOpen && (
              <>
                {showRadius && (
                  <InputRow>
                    <SelectInput
                      ariaLabel="Select a search radius"
                      id="selectRadiusSelect"
                      placeholder="Search radius"
                      label="Radius"
                      options={RADIUS_OPTIONS}
                      value={filters.radius}
                      onChange={handleFilterChange('radius')}
                      testID="selectRadius"
                    />
                  </InputRow>
                )}

                {showExpand && (
                  <InputRow>
                    <SelectInput
                      ariaLabel="Expand search radius"
                      id="expandRadiusSelect"
                      placeholder="Expand"
                      label="Expand"
                      options={EXPAND_OPTIONS}
                      value={filters.expand}
                      onChange={handleFilterChange('expand')}
                      testID="expandRadius"
                    />
                  </InputRow>
                )}

                <InputRow>
                  <InputColumn>
                    <SelectInput
                      ariaLabel="Select a category"
                      label="Select a category"
                      id="categorySelect"
                      options={CATEGORY_OPTIONS}
                      value={getCategoryOption(filters)}
                      onChange={handleFilterChange('category')}
                      testID="searchCategory"
                    />

                    <div style={{ flex: '0 0 175px' }}>
                      {currencyInput(filters, handleFilterChange)}

                      <SmallPartnership
                        href="/currency-transfers"
                        onClick={trackGaLink('currencies_direct_logo_click', 'Price Dropdown', 'SRP')}
                      >
                        <span>In partnership with</span>
                        <img src={currencyDirect.src} alt="Currency Symbol" />
                      </SmallPartnership>
                    </div>
                  </InputColumn>
                </InputRow>

                <InputColumn>
                  <PriceInputs
                    filters={filters}
                    minPriceOptions={minPriceOptions}
                    maxPriceOptions={maxPriceOptions}
                    handleFilterChange={handleFilterChange}
                    currencySymbol={currencySymbol}
                  />
                </InputColumn>

                {HAS_BEDROOMS_FILTER.includes(filters.category) && (
                  <InputColumn>
                    <BedInputs filters={filters} handleFilterChange={handleFilterChange} />
                  </InputColumn>
                )}

                <StyleInputs filters={filters} propertyStyles={propertyStyles} handleStyleChange={handleStyleChange} />

                <div>
                  <SubHeading align="left" fontSize={20} mb={15}>
                    Listing
                  </SubHeading>

                  <IncludeExcludeInputs filters={filters} handleFilterChange={handleFilterChange} isRental={isRental} />
                </div>

                {showFurnishingOptions && (
                  <div>
                    <Row style={{ width: '100%', marginTop: 20 }}>
                      <InputLabel>Furnishing</InputLabel>
                    </Row>

                    <InputRow style={{ gap: 15 }}>{furnishingInputs(filters, handleFilterChange, true)}</InputRow>
                  </div>
                )}

                {styleTags(filters, handleClearAttribute)}

                {['residential', 'newhomes', 'student', 'holidayhomes'].includes(filters.category) && (
                  <>
                    <SmartInputs
                      filters={filters}
                      handleFilterChange={handleFilterChange}
                      isNewHomes={isNewHomes}
                      searchParams={properties.data?.params}
                      isIE={isIE}
                    />

                    <PreferenceInputs
                      filters={filters}
                      handleFilterChange={handleFilterChange}
                      isRental={isRental}
                      isNewHomes={isNewHomes}
                    />
                  </>
                )}
              </>
            )}
          </ScrollDiv>

          <ModalFooter>
            <Button
              containerStyle={{ maxWidth: 160, height: 44 }}
              color={theme.backgroundDark}
              onClick={handleClearFilters}
              type="button"
            >
              Reset Filters {totalFilters > 0 ? `(${totalFilters})` : ''}
            </Button>

            <Button containerStyle={{ marginLeft: 15, maxWidth: 160, height: 44 }} type="submit">
              Apply Filters
            </Button>
          </ModalFooter>
        </SearchModal>
      )}

      <FilterContainer role="menubar">
        {activeFilterPane === 'more' && (
          <FiltersPane $overflow>
            <FilterPaneContent>
              <RowCenter w="100%" mt={20}>
                <Button
                  containerStyle={{ maxWidth: 160, height: 44 }}
                  color={theme.backgroundLight}
                  hoverTextColor={theme.white}
                  fontColor={theme.backgroundMid}
                  onClick={handleClearFilters}
                  type="button"
                >
                  Reset Filters
                </Button>

                <Button containerStyle={{ marginLeft: 15, maxWidth: 160, height: 44 }} type="submit">
                  Apply Filters
                </Button>
              </RowCenter>
            </FilterPaneContent>
          </FiltersPane>
        )}
      </FilterContainer>
    </form>
  );
};

export default SearchBar;
